import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSEO } from "@/hooks/useSEO";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6cpki2sgkaik1undpg";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const contactChannels = [
  {
    icon: "ri-mail-line",
    label: "客服邮箱",
    value: "support@51nav.com",
    tone: "bg-primary-100 text-primary-600",
  },
  {
    icon: "ri-time-line",
    label: "服务时间",
    value: "周一至周五 09:00 - 18:00",
    tone: "bg-secondary-100 text-secondary-700",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "北京市朝阳区互联网产业园 A 座 8 层",
    tone: "bg-accent-100 text-accent-700",
  },
];

export default function ContactPage() {
  useSEO(
    "联系我们 - 51网址导航",
    "联系51网址导航团队，提交网站收录申请、合作咨询与使用建议，我们会尽快回复。",
    "/contact",
  );

  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <div className="mt-5 text-center max-w-2xl mx-auto">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          联系我们
        </h1>
        <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
          无论是网站收录申请、合作咨询，还是使用建议，都欢迎给我们留言，我们会尽快回复。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-8">
        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-5">在线留言</h2>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={50}
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请简要描述您的问题或建议（最多 500 字）"
                  className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400 resize-none"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="contact-verify-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full sm:w-auto sm:self-start px-8 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === "submitting" ? "提交中…" : "提交留言"}
              </button>

              {status === "success" && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-secondary-100 border border-secondary-200 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  <span>提交成功！感谢您的留言，我们会尽快与您联系。</span>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-accent-100 border border-accent-200 text-sm text-accent-900">
                  <i className="ri-error-warning-line mt-0.5" />
                  <span>{formError || "提交失败，请稍后重试。"}</span>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">联系方式</h2>
            <ul className="space-y-4">
              {contactChannels.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${item.tone}`}>
                    <i className={item.icon} />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">{item.label}</span>
                    <span className="block text-sm text-foreground-900 mt-0.5">{item.value}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 text-center">
            <h2 className="font-heading font-bold text-lg text-foreground-950">官方微信</h2>
            <p className="text-xs text-foreground-500 mt-1.5 mb-4">扫码添加，获取网址收录与使用帮助</p>
            <div className="w-40 h-40 mx-auto rounded-xl border border-background-200 overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20black%20and%20white%20qr%20code%20pattern%20on%20white%20square%20background%2C%20sharp%20minimal%20design%2C%20high%20contrast%2C%20flat%20graphic&width=400&height=400&seq=nav-qr-01&orientation=squarish"
                alt="51网址导航官方微信二维码"
                title="51网址导航官方微信"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-foreground-400 mt-4">
              演示二维码，仅作展示用途
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}